import L from 'leaflet';

/** Status -> fill. These are the five states the icon set ships with. */
export const STATUS_COLORS = {
  on_time: '#5B9BD5',
  ahead: '#16A34A',
  at_port: '#F59E0B',
  delayed: '#DC2626',
  discharged: '#94A3B8',
};

const WHEEL_FILL = '#2F3A33';

/**
 * Sub-paths per mode. `fixed` marks parts that keep their own colour whatever
 * the status (the truck's wheels), so recolouring never turns them green.
 */
const MODE_SHAPES = {
  air: [
    {
      d: 'M100 19C107.6 19 112.6 31.4 112.6 47.4V74.8L188 119V137.6L112.6 118.4V151L134.2 166.8V180.2L100 170.6L65.8 180.2V166.8L87.4 151V118.4L12 137.6V119L87.4 74.8V47.4C87.4 31.4 92.4 19 100 19Z',
      strokeWidth: 11,
    },
  ],
  truck: [
    {
      d: 'M32 46H116C118.652 46 121.196 47.0536 123.071 48.9289C124.946 50.8043 126 53.3478 126 56V126H22C20.6868 126 19.3864 125.741 18.1732 125.239C16.9599 124.736 15.8575 124 14.9289 123.071C13.0536 121.196 12 118.652 12 116V56C12 53.3478 13.0536 50.8043 14.9289 48.9289C16.8043 47.0536 19.3478 46 22 46H32Z',
      strokeWidth: 11,
    },
    {
      d: 'M126 72H158C159.469 71.9976 160.921 72.319 162.252 72.9412C163.582 73.5634 164.76 74.4712 165.7 75.6L186 100C187.491 101.799 188.305 104.063 188.3 106.4V126H126V72Z',
      strokeWidth: 11,
    },
    {
      d: 'M71 153C80.3888 153 88 145.389 88 136C88 126.611 80.3888 119 71 119C61.6112 119 54 126.611 54 136C54 145.389 61.6112 153 71 153Z',
      strokeWidth: 9,
      fixed: WHEEL_FILL,
    },
    {
      d: 'M160 153C169.389 153 177 145.389 177 136C177 126.611 169.389 119 160 119C150.611 119 143 126.611 143 136C143 145.389 150.611 153 160 153Z',
      strokeWidth: 9,
      fixed: WHEEL_FILL,
    },
  ],
};

/**
 * Map a row to one of the five icon states.
 *
 * Air and truck rows only ever report in_transit / arrived — neither mode has
 * a delay signal, because neither has a carrier milestone feed to compare a
 * schedule against. `arrived` shows amber rather than grey on purpose: the
 * 4-day retention rule means the cargo is sitting AT the airport or crossing,
 * which is what "at port" depicts; grey "discharged" would imply it is gone.
 */
export function statusToIconState(row) {
  if (!row) return 'on_time';
  if (row.delayed === true) return 'delayed';
  if (row.ahead === true) return 'ahead';
  const status = String(row.status || '').toLowerCase();
  if (status === 'discharged') return 'discharged';
  if (status === 'arrived' || status === 'at_port') return 'at_port';
  return 'on_time';
}

/**
 * The plane points north in the artwork, so it rotates to its heading. The
 * truck is drawn in side view facing right: rotating it would put it on its
 * roof, so it mirrors instead when the heading points west.
 */
function transformFor(mode, heading) {
  const deg = Number.isFinite(heading) ? heading : 0;
  if (mode === 'truck') {
    return deg > 180 ? 'scale(-1,1) translate(-200,0)' : '';
  }
  return `rotate(${deg} 100 100)`;
}

/**
 * Leaflet divIcon for a mode marker.
 *
 * @param {object}  opts
 * @param {string}  opts.mode      'air' | 'truck'
 * @param {string}  opts.state     key of STATUS_COLORS
 * @param {number}  opts.heading   degrees, 0 = north
 * @param {boolean} opts.selected  draws the focus ring and a larger footprint
 */
const iconCache = new Map();

export function createModeIcon(opts = {}) {
  const { mode = 'air', state = 'on_time', heading = 0, selected = false, sizePx = null } = opts;
  const cacheKey = `${mode}|${state}|${Number.isFinite(heading) ? heading : 0}|${selected}|${sizePx}`;
  const hit = iconCache.get(cacheKey);
  if (hit) return hit;
  const icon = buildModeIcon({ mode, state, heading, selected, sizePx });
  iconCache.set(cacheKey, icon);
  return icon;
}

function buildModeIcon({ mode, state, heading, selected, sizePx }) {
  const shapes = MODE_SHAPES[mode] || MODE_SHAPES.air;
  const fill = STATUS_COLORS[state] || STATUS_COLORS.on_time;
  const size = sizePx || (selected ? 46 : 34);
  const half = size / 2;
  const transform = transformFor(mode, heading);

  const paths = shapes.map((s) => (
    `<path d="${s.d}" fill="${s.fixed || fill}" stroke="white" stroke-width="${s.strokeWidth}" stroke-linejoin="round"/>`
  )).join('');

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 200 200">`
    + `<g transform="${transform}">${paths}</g></svg>`;

  return L.divIcon({
    html: svg,
    className: `mode-marker mode-marker--${mode} mode-marker--${state}`,
    iconSize: [size, size],
    iconAnchor: [half, half],
    popupAnchor: [0, -half],
  });
}

export default createModeIcon;
